import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PublishedThreadsDialog } from './published-threads-dialog';
import { KimThreadStatus } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { Permission } from '../../../../../../../../../../backend/src/shared/action-registry';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteKimThread } from '@/api/mutations/useDeleteKimThread';
import { useRenameKimThread } from '@/api/mutations/useRenameKimThread';
import { useTogglePublishedKimThread } from '@/api/mutations/useTogglePublishedKimThreads';
import { useGetKimThreads } from '@/api/queries/useGetKimThreads';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

export function NewKimSidebar() {
  const { matterId, subsetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { i18n } = useTranslation();
  const { data: threads, isLoading } = useGetKimThreads(matterId, subsetId);
  const { mutate: deleteThread } = useDeleteKimThread(matterId, subsetId);
  const { mutate: renameThread } = useRenameKimThread(matterId, subsetId);
  const { mutate: togglePublished } = useTogglePublishedKimThread(matterId, subsetId);

  const [searchQuery, setSearchQuery] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingThread, setRenamingThread] = useState<{ id: string; name: string } | null>(null);

  const [collapsed, setCollapsed] = useState(false);

  const filteredThreads = threads?.filter((thread) => thread.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const publishedThreads = threads?.filter((t) => t.isPublished);
  const userThreads = filteredThreads?.filter((t) => t.createdById === user?.id);

  // threadId is not available from useParams because the sidebar sits outside the :threadId route
  const kimBasePath = `/matters/${matterId}/subsets/${subsetId}/new-kim/`;
  const activeThreadId = location.pathname.startsWith(kimBasePath)
    ? location.pathname.slice(kimBasePath.length).split('/')[0] || undefined
    : undefined;

  const navLinks: { slug: string; icon: string; label: string }[] = [
    { slug: 'work-product', icon: 'lucide--folder-closed', label: 'Work Product' },
  ];

  return (
    <div
      className={cn(
        collapsed
          ? 'w-12 min-w-12 rounded-3xl hover:cursor-pointer hover:border-gray-400/60'
          : 'w-64 min-w-64 rounded-lg',
        'relative z-20 m-2 flex flex-col gap-1.5 border bg-white pt-2 duration-100',
      )}
      onClick={() => setCollapsed(false)}
    >
      <div className="mx-2 flex items-center gap-1.5">
        <Button
          asChild
          size="sm"
          className={cn(
            collapsed ? 'rounded-2xl py-1.5' : 'py-1',
            'grow gap-0 transition-[color,border-color,background-color,padding,border-radius] duration-150',
          )}
        >
          <Link to={`/matters/${matterId}/subsets/${subsetId}/new-kim/`}>
            <span
              className={cn(collapsed ? 'w-0 opacity-0' : 'w-auto pr-1.5 opacity-100', 'line-clamp-1 duration-150')}
            >
              New Thread
            </span>
            <Icon className="lucide--plus" />
          </Link>
        </Button>

        {!collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={'ghost'}
                className="px-1 text-gray-500"
                onClick={(event) => {
                  event.stopPropagation();
                  setCollapsed(true);
                }}
              >
                <Icon className="lucide--sidebar-close" />
              </Button>
            </TooltipTrigger>

            <TooltipContent side="bottom">Close sidebar</TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col gap-0.5 py-0.5">
        {navLinks.map((link) => {
          const target = `${kimBasePath}${link.slug}`;
          const isActive = location.pathname === target || location.pathname.startsWith(`${target}/`);

          return (
            <Link
              key={link.slug}
              to={target}
              className={cn(
                'mx-2 flex items-center justify-between gap-2 overflow-hidden rounded-md px-2 py-1.25 text-xs ring-offset-2 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-teal-500',
                isActive ? 'font-medium text-gray-800' : 'text-gray-600',
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className={cn(isActive && 'text-teal-500', link.icon, 'text-sm duration-150')} />
                <div className={cn(collapsed ? 'opacity-0' : 'opacity-100', 'line-clamp-1 break-all duration-150')}>
                  {link.label}
                </div>
              </div>

              <div
                className={cn(
                  collapsed ? 'opacity-0' : 'opacity-100',
                  isActive ? 'opacity-100' : 'opacity-0',
                  'mr-0.25 size-1 rounded-full bg-teal-500 duration-150',
                )}
              />
            </Link>
          );
        })}

        <PublishedThreadsDialog publishedThreads={publishedThreads ?? []} collapsed={collapsed} />
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            key="sidebar-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="flex min-h-0 grow flex-col"
          >
            <div
              className={cn(
                'mx-2 flex items-center rounded-md border border-gray-300 bg-white ring-offset-2 transition-colors duration-150 focus-within:ring focus-within:ring-teal-500',
              )}
            >
              <Icon className="lucide--search mx-2.25 mr-1.5 text-xs text-gray-400" />
              <input
                type="text"
                placeholder="Search threads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full py-1.25 text-xs text-gray-900 outline-none placeholder:text-gray-400 focus:outline-none"
              />
            </div>

            {isLoading ? (
              <p className="my-8 animate-pulse text-center text-xs text-gray-400">Loading Threads...</p>
            ) : threads?.length === 0 ? (
              <div className="my-8 flex flex-col items-center gap-6.5 px-4 text-xs text-gray-400">
                <p className="text-center">No threads yet</p>
              </div>
            ) : searchQuery && filteredThreads?.length === 0 ? (
              <p className="my-8 text-center text-xs text-gray-400">No matching threads</p>
            ) : null}

            <div className="flex grow flex-col justify-between overflow-y-auto">
              <div className="flex flex-col gap-1 overflow-y-auto p-2 pt-1 [scrollbar-width:none]">
                {userThreads &&
                  userThreads.length > 0 &&
                  userThreads.map((thread) => (
                    <div
                      key={thread.id}
                      className={cn(
                        activeThreadId === thread.id
                          ? 'bg-gray-100 font-medium text-gray-900'
                          : 'text-gray-600 hover:bg-gray-50',
                        'flex items-center justify-between rounded-md pr-1 text-xs ring-offset-2 transition-colors first:mt-1 has-[>a:focus-visible]:ring-1 has-[>a:focus-visible]:ring-teal-500',
                      )}
                    >
                      <Tooltip delayDuration={400}>
                        <TooltipTrigger asChild>
                          <Link
                            to={`/matters/${matterId}/subsets/${subsetId}/new-kim/${thread.id}`}
                            className="w-full truncate py-1.5 pl-2 focus-visible:outline-none"
                          >
                            {thread.name}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="right" sideOffset={38} className="p-2 px-2.5">
                          <p className="mb-0.5 font-medium">
                            {new Date(thread.createdDate).toLocaleDateString(i18n.language, { dateStyle: 'long' })}
                          </p>
                          <p className="text-gray-600">{thread.name}</p>
                          {thread.isLegacy && <p className="mt-0.5 text-gray-400">Legacy thread (read-only)</p>}
                        </TooltipContent>
                      </Tooltip>

                      {thread.isLegacy && <Icon className="lucide--lock mr-1 ml-1.5 text-gray-400" />}

                      {thread.status === KimThreadStatus.Failed && (
                        <Icon className="lucide--circle-alert mr-1 ml-1.5 text-red-500" />
                      )}

                      {thread.isPublished && <Icon className="lucide--users-round mr-1 ml-1.5" />}

                      {thread.createdById === user?.id && (
                        <DropdownMenu size="sm">
                          <DropdownMenuTrigger className="rounded-md px-1 ring-offset-2 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none">
                            <Icon className="lucide--more-horizontal" />{' '}
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align="start" alignOffset={-8} side="right" sideOffset={8}>
                            <DropdownMenuItem
                              onClick={() => {
                                setRenamingThread({ id: thread.id, name: thread.name });
                                setRenameDialogOpen(true);
                              }}
                            >
                              <Icon className="lucide--pencil" />
                              Rename
                            </DropdownMenuItem>
                            <PermissionGate
                              permission={Permission.ThreadPublish}
                              uiMode="disabled"
                              disabledMessage="You don't have permission to publish a thread"
                            >
                              <DropdownMenuItem onClick={() => togglePublished(thread.id)}>
                                <Icon className="lucide--users-round" />
                                {thread.isPublished ? 'Unpublish' : 'Publish'}
                              </DropdownMenuItem>
                            </PermissionGate>
                            <DropdownMenuItem
                              onClick={() =>
                                deleteThread(thread.id, {
                                  onSuccess: () => {
                                    if (activeThreadId === thread.id) {
                                      navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/`);
                                    }
                                  },
                                })
                              }
                            >
                              <Icon className="lucide--trash" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {renamingThread && (
        <EditFieldDialogControlled
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          title="Rename thread"
          description="Enter a new name for this thread."
          fieldLabel="Name"
          currentValue={renamingThread.name}
          onSave={(name) => {
            trackCustomEvent(TrackingEventsV2.KimRenameThread, { matterId, subsetId, threadId: renamingThread.id });
            renameThread({ threadId: renamingThread.id, name });
          }}
        />
      )}
    </div>
  );
}
